import React, { useEffect } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { BOTTOM_INDENTS, COLORS, ROUTE_PATHS } from '@constants';
import { CAMPAIGN_TYPE_ID } from '@enums';
import { useStore } from '@hooks';
import { Loader, Typography } from '@ui';
import { Form, Layout } from 'antd';
import { observer } from 'mobx-react';
import AiSetup from '../../../../components/AiSetup';
import Preview from '../../components/Preview';
import ProductsSelection from '../../components/ProductsSelection';
import { useFormInit } from '../../form';
import { useCalculatePriority } from '../../hooks/useCalculatePriority';
import { PAGE } from './constants';

const { Sider, Content } = Layout;

const Step2 = observer(() => {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const params = useParams();

    const store = useStore();
    const {
        campaign,
        getCampaign,
        syncSelectionWithServer,
        setFormSubmitting,
        formSubmitting,
        productsSelectionPending,
        setIsNextDisabled,
        setIsNextLoading,
        previewProductsLength
    } = store.campaignWizardStore;

    const showAIModal = !!searchParams.get('ai');

    const form = useFormInit();
    const calculatePriority = useCalculatePriority();

    const nextDisabled = !previewProductsLength;

    const nextLoading = formSubmitting || productsSelectionPending;

    useEffect(() => {
        const campaignId = params?.id ? Number(params.id) : null;

        if (!campaignId || isNaN(campaignId)) {
            return;
        }

        if (!campaign) {
            getCampaign(campaignId);
            return;
        }

        if (campaign.id && campaign.id === campaignId && !campaign.priority) {
            calculatePriority({}, false, campaignId);
        }
    }, [params?.id, campaign, getCampaign]);

    useEffect(() => {
        setIsNextLoading(nextLoading);
    }, [nextLoading]);

    useEffect(() => {
        setIsNextDisabled(nextDisabled);
    }, [nextDisabled]);

    const onFinish = async () => {
        setFormSubmitting(true);
        await syncSelectionWithServer();
        setFormSubmitting(false);

        const { error } = store.campaignWizardStore;
        if (!error && campaign) {
            navigate(ROUTE_PATHS.CAMPAIGNS_CREATE_STEP_3.replace(':id', campaign.id.toString()));
        }
    };

    const creative = campaign?.creatives?.[0] || {};

    if (!campaign) {
        return <Loader size='120px' />;
    }

    return (
        <Layout hasSider style={{ justifyContent: 'space-between', gap: 40 }}>
            {showAIModal ? <AiSetup /> : null}
            <Form form={form} onFinish={onFinish}>
                <Content tagName='div' style={{ maxWidth: 867 }}>
                    <Typography.Title data-e2e='campaignwizard_step-2_title' style={BOTTOM_INDENTS.M}>
                        {PAGE.TITLE}
                    </Typography.Title>
                    <Typography.Text
                        data-e2e='campaignwizard_step-2_description'
                        type='body2'
                        style={BOTTOM_INDENTS.XS}
                        color={COLORS.$gray60Black}
                    >
                        {campaign?.catalogType === CAMPAIGN_TYPE_ID.SPECIFIC_PRODUCTS
                            ? PAGE.DESCRIPTION1_SPECIFIC_PRODUCTS
                            : PAGE.DESCRIPTION1_COMMON}
                    </Typography.Text>
                    <Typography.Text
                        data-e2e='campaignwizard_step-2_hint'
                        type='body2'
                        style={BOTTOM_INDENTS.M}
                        color={COLORS.$gray60Black}
                    >
                        {PAGE.DESCRIPTION2}
                    </Typography.Text>
                    <ProductsSelection />
                </Content>
            </Form>
            <Sider width='300px' style={{ backgroundColor: 'unset' }}>
                <Preview
                    offerMessage={creative.offerMessage}
                    font={creative.fontName}
                    secondaryColor={creative.secondaryColor}
                    primaryColor={creative.primaryColor}
                    logo={creative.logoUrl}
                    brandLink={store.accountsStore.customShopDomain}
                />
            </Sider>
        </Layout>
    );
});

export default Step2;
